import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard,
  Ticket,
  Trophy,
  Settings as SettingsIcon,
  Users,
  LogOut,
  Save,
  Plus,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
  CheckCircle2,
  Bell,
  Shield,
  ExternalLink,
  CreditCard,
  Package,
  MessageSquare
} from "lucide-react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useAdmin } from "../../contexts/AdminContext";
import { 
  uploadFile, 
  fetchAdminConversations, 
  fetchChatMessages, 
  sendChatMessage, 
  markChatAsRead,
  apiFetchOrders,
  apiUpdateOrderStatus
} from "../../lib/api";

interface ImageInputProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
}

const ImageInput = ({ label, value, onChange }: ImageInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const publicUrl = await uploadFile(file);
      onChange(publicUrl);
    } catch (err) {
      console.error("Upload error:", err);
      alert("Fehler beim Hochladen des Bildes.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5 pointer-events-auto">
      <div className="flex items-center gap-2">
        <label className="block text-[10px] font-bold text-slate-400 uppercase">{label}</label>
        {value && (
          <div className="w-4 h-4 rounded-full overflow-hidden border border-slate-200">
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary transition-colors"
          placeholder="Bild-Link oder Upload"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`${uploading ? "bg-slate-50 text-slate-400" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors disabled:cursor-not-allowed cursor-pointer`}
        >
          {uploading ? (
            <span className="w-3 h-3 border-2 border-slate-300 border-t-primary rounded-full animate-spin"></span>
          ) : (
            <Upload size={12} />
          )}
          {uploading ? "..." : "Upload"}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileUpload}
        />
      </div>
    </div>
  );
};

type Tab = "dashboard" | "packages" | "prizes" | "users" | "orders" | "settings" | "tickets" | "chat";

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState("");
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const { settings, updateSettings, saveSettings } = useAdmin();

  // Chat Polling
  useEffect(() => {
    if (activeTab === "chat") {
      const loadConvs = async () => {
        try {
          const data = await fetchAdminConversations();
          setConversations(data);
        } catch (e) { console.error(e); }
      };
      loadConvs();
      const iv = setInterval(loadConvs, 2000);
      return () => clearInterval(iv);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "chat" && activeSession) {
      const loadMsgs = async () => {
        try {
          const data = await fetchChatMessages(activeSession);
          setChatMessages(data);
        } catch (e) { console.error(e); }
      };
      loadMsgs();
      const iv = setInterval(loadMsgs, 3000);
      return () => {
        clearInterval(iv);
        markChatAsRead(activeSession).catch(console.error);
      };
    }
  }, [activeTab, activeSession]);

  useEffect(() => {
    if (activeTab === "chat" && chatMessages.length > 0) {
      chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages.length, activeTab]);

  useEffect(() => {
    // Poll orders every 30 seconds if and only if we are in relevant tabs
    if (activeTab === "dashboard" || activeTab === "orders" || activeTab === "tickets") {
      const loadOrders = async () => {
        try {
          const data = await apiFetchOrders();
          setOrders(data);
        } catch (e) {
          console.error("Orders fetch failed:", e);
        }
      };
      loadOrders();
      const iv = setInterval(loadOrders, 30000);
      return () => clearInterval(iv);
    }
  }, [activeTab]);

  const dashboardStats = {
    revenue: orders
      .filter(o => o.payment_status === "paid")
      .reduce((acc, o) => acc + (parseFloat(o.amount) || 0), 0),
    tickets: orders
      .filter(o => o.payment_status === "paid")
      .reduce((acc, o) => acc + (o.ticket_count || 1), 0),
    users: new Set(orders.map(o => o.user_email)).size
  };

  const sendAdminReply = async () => {
    if (!chatInput.trim() || !activeSession) return;
    try {
      await sendChatMessage(activeSession, 'admin', chatInput.trim());
      setChatInput("");
      const updated = await fetchChatMessages(activeSession);
      setChatMessages(updated);
    } catch (e) { console.error(e); }
  };

  const updateOrderStatus = async (id: number, status: string) => {
    try {
      await apiUpdateOrderStatus(id, status);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, payment_status: status } : o));
    } catch (e) {
      console.error(e);
      alert("Fehler beim Aktualisieren");
    }
  };

  const handleSave = async () => {
    try {
      setSaveStatus("Speichern...");
      await saveSettings();
      setSaveStatus("Änderungen gespeichert!");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error(e);
      setSaveStatus("Fehler beim Speichern.");
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    window.location.href = "/admin/login";
  };

  const SidebarItem = ({ id, icon: Icon, label }: { id: Tab, icon: any, label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer pointer-events-auto ${activeTab === id
          ? "bg-primary text-white shadow-lg shadow-orange-200"
          : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        }`}
    >
      <Icon size={20} />
      <span className="font-semibold text-sm">{label}</span>
    </button>
  );

  return (
    <main className="min-h-screen bg-slate-50 font-inter relative z-[1]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 pt-24 pb-12 relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 relative z-10">          {/* Sidebar — Vertical on desktop, horizontal scrollable tabs on mobile */}
          <aside className="w-full lg:w-64 shrink-0 relative z-20 pointer-events-auto">
            {/* Mobile: horizontal scrollable tab bar */}
            <div className="flex lg:hidden overflow-x-auto gap-2 pb-3 no-scrollbar">
              {([
                { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
                { id: "orders", icon: CreditCard, label: "Bestellungen" },
                { id: "tickets", icon: Ticket, label: "Lose" },
                { id: "packages", icon: Package, label: "Pakete" },
                { id: "prizes", icon: Trophy, label: "Gewinne" },
                { id: "users", icon: Users, label: "Benutzer" },
                { id: "chat", icon: MessageSquare, label: "Support" },
                { id: "settings", icon: SettingsIcon, label: "Einstellungen" },
              ] as { id: Tab, icon: any, label: string }[]).map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab === id ? "bg-primary text-white shadow-lg" : "bg-white border border-slate-200 text-slate-500"}`}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>

            {/* Desktop: vertical sidebar */}
            <div className="hidden lg:flex flex-col gap-2">
              <SidebarItem id="dashboard" icon={LayoutDashboard} label="Dashboard" />
              <SidebarItem id="orders" icon={CreditCard} label="Bestellungen" />
              <SidebarItem id="tickets" icon={Ticket} label="Alle Lose" />
              <SidebarItem id="packages" icon={Package} label="Paketverwaltung" />
              <SidebarItem id="prizes" icon={Trophy} label="Gewinne & Ziehung" />
              <SidebarItem id="users" icon={Users} label="Benutzer" />
              <SidebarItem id="chat" icon={MessageSquare} label="Support-Chat" />
              <SidebarItem id="settings" icon={SettingsIcon} label="Einstellungen" />

              <div className="mt-8 pt-8 border-t border-slate-200">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-all cursor-pointer pointer-events-auto"
                >
                  <LogOut size={20} />
                  <span className="font-semibold text-sm">Abmelden</span>
                </button>
              </div>
            </div>

            {/* Mobile logout */}
            <div className="flex lg:hidden justify-end mt-2">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-red-500 border border-red-100 bg-white text-xs font-bold cursor-pointer"
              >
                <LogOut size={14} />
                Abmelden
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <section className="flex-1 min-w-0 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm relative z-20 pointer-events-auto">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900 font-playfair capitalize">
                {activeTab === "dashboard" && "Dashboard"}
                {activeTab === "orders" && "Bestellungsverwaltung"}
                {activeTab === "tickets" && "Lose-Datenbank"}
                {activeTab === "packages" && "Paketverwaltung"}
                {activeTab === "prizes" && "Gewinn- & Ziehungskonfig."}
                {activeTab === "users" && "Benutzerverwaltung"}
                {activeTab === "settings" && "Seiteneinstellungen"}
                {activeTab === "chat" && "Live-Support"}
              </h1>
              <div className="flex items-center gap-4">
                {saveStatus && <span className="text-xs text-secondary-dark font-medium animate-pulse">{saveStatus}</span>}
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800 transition-all cursor-pointer"
                >
                  <Save size={16} />
                  Speichern
                </button>
              </div>
            </div>

            {activeTab === "dashboard" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Gesamtumsatz</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardStats.revenue.toLocaleString('de-DE')} €</p>
                  <p className="text-secondary-dark text-[10px] font-bold mt-2">Basierend auf {orders.filter(o => o.payment_status === 'paid').length} Verkäufen</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Verkaufte Lose</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardStats.tickets.toLocaleString('de-DE')}</p>
                  <p className="text-slate-400 text-[10px] mt-2">Inklusive Bonuslose</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Teilnehmer</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardStats.users.toLocaleString('de-DE')}</p>
                  <p className="text-secondary-dark text-[10px] font-bold mt-2">Einzigartige E-Mails</p>
                </div>
              </div>
            )}

            {activeTab === "packages" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-slate-800">Paketübersicht</h3>
                  <button
                    onClick={() => updateSettings({ packages: [...settings.packages, { name: "Neues Paket", count: 1, price: "10€" }] })}
                    className="text-primary flex items-center gap-1 text-sm font-bold cursor-pointer"
                  >
                    <Plus size={16} /> Paket hinzufügen
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {settings.packages.map((p, i) => (
                    <div key={i} className="flex flex-col md:flex-row items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 group">
                      <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={p.name}
                          onChange={(e) => {
                            const newP = [...(settings.packages || [])];
                            newP[i] = { ...newP[i], name: e.target.value };
                            updateSettings({ packages: newP });
                          }}
                          className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          placeholder="Paketname"
                        />
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={p.count}
                            onChange={(e) => {
                              const newP = [...(settings.packages || [])];
                              newP[i] = { ...newP[i], count: parseInt(e.target.value) || 0 };
                              updateSettings({ packages: newP });
                            }}
                            className="w-20 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="Tickets"
                          />
                          <input
                            type="text"
                            value={p.price}
                            onChange={(e) => {
                              const newP = [...(settings.packages || [])];
                              newP[i] = { ...newP[i], price: e.target.value };
                              updateSettings({ packages: newP });
                            }}
                            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="Preis"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-bold text-slate-500 uppercase">Beliebt</label>
                          <input
                            type="checkbox"
                            checked={p.popular}
                            onChange={(e) => {
                              const newP = [...settings.packages];
                              newP[i].popular = e.target.checked;
                              updateSettings({ packages: newP });
                            }}
                            className="w-4 h-4 secondary-light-primary"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => updateSettings({ packages: settings.packages.filter((_, idx) => idx !== i) })}
                        className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "prizes" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-800 border-b pb-2">Immobilien-Informationen (Hauptgewinn)</h3>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Immobilientitel</label>
                    <input
                      type="text"
                      value={settings.propertyTitle}
                      onChange={(e) => updateSettings({ propertyTitle: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                      placeholder="Ex: Exklusive Villa — Seeshaupt"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Lage (Stadt · Land)</label>
                    <input
                      type="text"
                      value={settings.propertyLocation}
                      onChange={(e) => updateSettings({ propertyLocation: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none"
                      placeholder="Ex: Seeshaupt · Deutschland"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Vollständige Beschreibung</label>
                    <textarea
                      value={settings.propertyDescription}
                      onChange={(e) => updateSettings({ propertyDescription: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm h-32 outline-none"
                      placeholder="Beschreiben Sie die Immobilie im Detail..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Gesamtwert</label>
                      <input
                        type="text"
                        value={settings.lotteryValue}
                        onChange={(e) => updateSettings({ lotteryValue: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Surface (m²)</label>
                      <input
                        type="text"
                        value={settings.propertySurface}
                        onChange={(e) => updateSettings({ propertySurface: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Terrain (m²)</label>
                      <input
                        type="text"
                        value={settings.propertyLand}
                        onChange={(e) => updateSettings({ propertyLand: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Schlafzimmer</label>
                      <input
                        type="text"
                        value={settings.propertyRooms}
                        onChange={(e) => updateSettings({ propertyRooms: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Badezimmer</label>
                      <input
                        type="text"
                        value={settings.propertyBathrooms}
                        onChange={(e) => updateSettings({ propertyBathrooms: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Highlights</h3>
                      <button
                        onClick={() => updateSettings({ propertyHighlights: [...settings.propertyHighlights, "Neues Argument"] })}
                        className="text-primary text-xs font-bold flex items-center gap-1"
                      >
                        <Plus size={14} /> Hinzufügen
                      </button>
                    </div>
                    <div className="space-y-2">
                      {settings.propertyHighlights.map((h, i) => (
                        <div key={i} className="flex gap-2">
                          <input
                            type="text"
                            value={h}
                            onChange={(e) => {
                              const next = [...settings.propertyHighlights];
                              next[i] = e.target.value;
                              updateSettings({ propertyHighlights: next });
                            }}
                            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs outline-none"
                          />
                          <button
                            onClick={() => {
                              const next = settings.propertyHighlights.filter((_, idx) => idx !== i);
                              updateSettings({ propertyHighlights: next });
                            }}
                            className="p-1.5 text-slate-300 hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Partner (Logo-Namen)</label>
                    <input
                      type="text"
                      value={settings.partners.join(", ")}
                      onChange={(e) => updateSettings({ partners: e.target.value.split(",").map(s => s.trim()) })}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none"
                    />
                    <p className="text-[10px] text-slate-400 mt-2">Namen durch Kommas trennen.</p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4">
                    <div className="flex items-center gap-2 border-b pb-2 mb-2">
                      <ImageIcon size={16} className="text-primary" />
                      <h3 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Bildverwaltung</h3>
                    </div>

                    <ImageInput
                      label="Hero-Bild (Startseite)"
                      value={settings.propertyImages.hero}
                      onChange={(val) => updateSettings({ propertyImages: { ...settings.propertyImages, hero: val } })}
                    />

                    <div className="flex flex-col gap-4">
                      <ImageInput
                        label="Exterieur-Showcase-Bild"
                        value={settings.propertyImages.showcaseExterior}
                        onChange={(val) => updateSettings({ propertyImages: { ...settings.propertyImages, showcaseExterior: val } })}
                      />
                      <ImageInput
                        label="Interieur-Showcase-Bild"
                        value={settings.propertyImages.showcaseInterior}
                        onChange={(val) => updateSettings({ propertyImages: { ...settings.propertyImages, showcaseInterior: val } })}
                      />
                      <ImageInput
                        label="Grundstück-Showcase-Bild"
                        value={settings.propertyImages.showcaseLand}
                        onChange={(val) => updateSettings({ propertyImages: { ...settings.propertyImages, showcaseLand: val } })}
                      />
                    </div>

                    <div className="pt-4 border-t border-slate-100">
                      <div className="flex justify-between items-center mb-4">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase">Fotogalerie</label>
                        <button
                          onClick={() => updateSettings({ propertyImages: { ...settings.propertyImages, gallery: [...settings.propertyImages.gallery, ""] } })}
                          className="bg-orange-50 text-primary-dark hover:bg-orange-100 px-3 py-1 rounded-full text-[10px] font-bold transition-all"
                        >
                          + Foto hinzufügen
                        </button>
                      </div>
                      <div className="flex flex-col gap-4">
                        {settings.propertyImages.gallery.map((url, i) => (
                          <div key={i} className="flex flex-col gap-2 p-3 bg-white border border-slate-100 rounded-xl relative group">
                            <button
                              onClick={() => {
                                const next = settings.propertyImages.gallery.filter((_, idx) => idx !== i);
                                updateSettings({ propertyImages: { ...settings.propertyImages, gallery: next } });
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                            >
                              <X size={12} />
                            </button>

                            {url && (
                              <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-100 mb-1">
                                <img src={url} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                              </div>
                            )}

                            <ImageInput
                              label={`Photo ${i + 1}`}
                              value={url}
                              onChange={(val) => {
                                const next = [...settings.propertyImages.gallery];
                                next[i] = val;
                                updateSettings({ propertyImages: { ...settings.propertyImages, gallery: next } });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-2xl p-6 text-white flex flex-col items-center justify-center text-center gap-4">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/25">
                      <Trophy size={32} />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-1">Simuler le Tirage</h4>
                      <p className="text-slate-400 text-xs">Tester l'affichage du gagnant.</p>
                    </div>
                    <button className="bg-white text-slate-900 px-6 py-2 rounded-full text-xs font-bold hover:bg-slate-100 transition-all cursor-pointer">
                      Lancer le Test
                    </button>
                  </div>
                </div>

                {/* Secondary Prizes UI in right column or end of left column */}
                <div className="col-span-1 md:col-span-2 mt-8 border-t border-slate-200 pt-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-slate-800 text-lg">Autres Lots (Secondaires)</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {settings.prizes.slice(1).map((prize, i) => (
                      <div key={prize.id} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex flex-col gap-5">
                        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-slate-200">
                          <input type="text" value={prize.tag} onChange={(e) => {
                            const next = [...settings.prizes];
                            next[i + 1].tag = e.target.value;
                            updateSettings({ prizes: next });
                          }} className="w-1/3 bg-transparent text-xs font-bold px-2 outline-none uppercase" placeholder="TAG" />
                          <input type="text" value={prize.tagColor} onChange={(e) => {
                            const next = [...settings.prizes];
                            next[i + 1].tagColor = e.target.value;
                            updateSettings({ prizes: next });
                          }} className="w-1/3 bg-slate-100 text-xs px-2 py-1 rounded outline-none" placeholder="Tailwind color" />
                        </div>

                        <div className="space-y-4">
                          <input type="text" value={prize.title} onChange={(e) => {
                            const next = [...settings.prizes];
                            next[i + 1].title = e.target.value;
                            updateSettings({ prizes: next });
                          }} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold" placeholder="Titel des Gewinns" />

                          <input type="text" value={prize.value} onChange={(e) => {
                            const next = [...settings.prizes];
                            next[i + 1].value = e.target.value;
                            updateSettings({ prizes: next });
                          }} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-primary font-bold" placeholder="Valeur (ex: 95 000 €)" />

                          <textarea value={prize.description || ""} onChange={(e) => {
                            const next = [...settings.prizes];
                            next[i + 1].description = e.target.value;
                            updateSettings({ prizes: next });
                          }} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs h-24" placeholder="Ausführliche Beschreibung des Gewinns..." />

                          <div className="space-y-2">
                             <div className="flex justify-between items-center text-[10px] uppercase font-bold text-slate-400">
                                <span>Spécifications (Label: Valeur)</span>
                                <button type="button" onClick={() => {
                                   const next = [...settings.prizes];
                                   next[i+1].specifications = [...(next[i+1].specifications || []), { label: "Label", value: "Valeur", icon: "ri-check-line" }];
                                   updateSettings({ prizes: next });
                                }} className="text-primary">+ Ajouter</button>
                             </div>
                             {(prize.specifications || []).map((spec, sIdx) => (
                               <div key={sIdx} className="flex gap-2">
                                 <input type="text" value={spec.label} onChange={(e) => {
                                    const next = [...settings.prizes];
                                    next[i+1].specifications[sIdx].label = e.target.value;
                                    updateSettings({ prizes: next });
                                 }} className="w-1/2 p-2 bg-white border border-slate-100 rounded text-[10px]" placeholder="Label" />
                                 <input type="text" value={spec.value} onChange={(e) => {
                                    const next = [...settings.prizes];
                                    next[i+1].specifications[sIdx].value = e.target.value;
                                    updateSettings({ prizes: next });
                                 }} className="w-1/2 p-2 bg-white border border-slate-100 rounded text-[10px]" placeholder="Valeur" />
                                 <button onClick={() => {
                                    const next = [...settings.prizes];
                                    next[i+1].specifications = next[i+1].specifications.filter((_:any, idx:number) => idx !== sIdx);
                                    updateSettings({ prizes: next });
                                 }} className="text-red-300 hover:text-red-500"><Trash2 size={12} /></button>
                               </div>
                             ))}
                          </div>

                          <ImageInput
                            label="Image principale"
                            value={prize.img}
                            onChange={(val) => {
                              const next = [...settings.prizes];
                              next[i + 1].img = val;
                              updateSettings({ prizes: next });
                            }}
                          />

                          <div className="pt-2">
                             <div className="flex justify-between items-center mb-2">
                                <label className="text-[10px] uppercase font-bold text-slate-400">Galerie Photos</label>
                                <button onClick={() => {
                                   const next = [...settings.prizes];
                                   next[i+1].gallery = [...(next[i+1].gallery || []), ""];
                                   updateSettings({ prizes: next });
                                }} className="text-primary text-[10px]">+ Ajouter</button>
                             </div>
                             <div className="grid grid-cols-2 gap-2">
                                {(prize.gallery || []).map((gImg, gIdx) => (
                                   <div key={gIdx} className="relative group">
                                      <ImageInput
                                         label=""
                                         value={gImg}
                                         onChange={(val) => {
                                            const next = [...settings.prizes];
                                            next[i+1].gallery[gIdx] = val;
                                            updateSettings({ prizes: next });
                                         }}
                                      />
                                      <button onClick={() => {
                                         const next = [...settings.prizes];
                                         next[i+1].gallery = next[i+1].gallery.filter((_:any, idx:number) => idx !== gIdx);
                                         updateSettings({ prizes: next });
                                      }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={8} /></button>
                                   </div>
                                ))}
                             </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "tickets" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-slate-800">Base de données des Tickets</h3>
                  <div className="bg-slate-50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500">
                    Total: {orders.reduce((acc, o) => acc + (o.ticket_count || 0), 0)}
                  </div>
                </div>
                <div className="overflow-x-auto bg-white border border-slate-100 rounded-2xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Kunde</th>
                        <th className="text-left py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Los-Code</th>
                        <th className="text-left py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Bestellreferenz</th>
                        <th className="text-center py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-slate-400">Keine Lose generiert.</td>
                        </tr>
                      ) : (
                        orders.flatMap(o => (o.tickets || []).map((t: string) => ({
                          ticket: t,
                          user: o.user_name,
                          email: o.user_email,
                          orderId: o.payment_id || o.id,
                          status: o.payment_status
                        }))).map((item, i) => (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="py-4 px-4 text-xs font-medium">{item.user} ({item.email})</td>
                            <td className="py-4 px-4 font-mono text-primary-dark font-bold">{item.ticket}</td>
                            <td className="py-4 px-4 text-xs text-slate-400">{item.orderId}</td>
                            <td className="py-4 px-4 text-center">
                               <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${item.status === 'paid' ? 'bg-emerald-100 text-secondary-dark' : 'bg-amber-100 text-amber-600'}`}>
                                 {item.status}
                               </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "users" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-slate-800">Utilisateurs enregistrés</h3>
                  <div className="bg-slate-50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500">
                    Total: 1,452
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left py-4 font-semibold uppercase tracking-widest text-[10px]">Utilisateur</th>
                      <th className="text-left py-4 font-semibold uppercase tracking-widest text-[10px]">Email</th>
                      <th className="text-left py-4 font-semibold uppercase tracking-widest text-[10px]">Tickets</th>
                      <th className="text-right py-4 font-semibold uppercase tracking-widest text-[10px]">Dernier achat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "Jean Dupont", email: "j.dupont@gmail.com", tickets: 25, date: "21/03/2026" },
                      { name: "Marc Lefebvre", email: "m-lefebvre42@outlook.fr", tickets: 5, date: "20/03/2026" },
                      { name: "Sarah Müller", email: "sarah.m@web.de", tickets: 120, date: "19/03/2026" },
                    ].map((u, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-4 font-semibold text-slate-800">{u.name}</td>
                        <td className="py-4 text-slate-500">{u.email}</td>
                        <td className="py-4"><span className="bg-orange-100 text-primary-dark font-bold px-2 py-1 rounded text-xs">{u.tickets}</span></td>
                        <td className="py-4 text-right text-slate-400">{u.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "orders" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-slate-800">Bestellverlauf</h3>
                  <div className="bg-slate-50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500">
                    Gesamt: {orders.length}
                  </div>
                </div>
                <div className="overflow-x-auto bg-white border border-slate-100 rounded-2xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Kunde</th>
                        <th className="text-left py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Paket</th>
                        <th className="text-right py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Betrag</th>
                        <th className="text-center py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Methode</th>
                        <th className="text-center py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Status</th>
                        <th className="text-center py-4 px-4 font-semibold uppercase tracking-widest text-[10px]">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-slate-400">Aucune commande trouvée.</td>
                        </tr>
                      ) : (
                        orders.map((o) => (
                          <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="py-4 px-4">
                              <p className="font-bold text-slate-800">{o.user_name}</p>
                              <p className="text-xs text-slate-400">{o.user_email}</p>
                            </td>
                            <td className="py-4 px-4 align-top">
                              <span className="bg-orange-100 text-primary-dark font-bold px-2 py-1 rounded text-xs">{o.package_id}</span>
                              <div className="mt-3 flex flex-wrap gap-1 max-w-[200px]">
                                {o.tickets && Array.isArray(o.tickets) && o.tickets.length > 0 ? (
                                  <>
                                    {o.tickets.slice(0, 3).map((t: string, i: number) => (
                                      <span key={i} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1 py-0.5 rounded border border-slate-200">
                                        {t}
                                      </span>
                                    ))}
                                    {o.tickets.length > 3 && (
                                      <span className="text-[10px] font-mono bg-slate-100 text-slate-400 px-1 py-0.5 rounded border border-slate-200" title={o.tickets.slice(3).join('\n')}>
                                        +{o.tickets.length - 3}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-[10px] italic text-slate-400">
                                    {o.payment_status === 'paid' ? 'Formatierung...' : 'Warten auf Zahlung'}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-right font-medium">
                              {o.amount} €
                            </td>
                            <td className="py-4 px-4 text-center">
                              {o.payment_method === 'card' && <span className="text-indigo-600 text-xs font-bold"><i className="ri-bank-card-line"></i> Karte</span>}
                              {o.payment_method === 'paypal' && <span className="text-blue-600 text-xs font-bold"><i className="ri-paypal-line"></i> PayPal</span>}
                              {o.payment_method === 'transfer' && <span className="text-secondary-dark text-xs font-bold"><i className="ri-building-line"></i> Überweisung</span>}
                            </td>
                            <td className="py-4 px-4 text-center">
                              {o.payment_status === 'paid' && <span className="bg-emerald-100 text-secondary-dark px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Bezahlt</span>}
                              {o.payment_status === 'pending' && <span className="bg-amber-100 text-amber-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Wartend</span>}
                              {o.payment_status === 'failed' && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Fehlgeschlagen</span>}
                            </td>
                            <td className="py-4 px-4 text-center">
                              {o.payment_status === 'pending' && o.payment_method === 'transfer' ? (
                                <button
                                  onClick={() => updateOrderStatus(o.id, 'paid')}
                                  className="bg-secondary hover:bg-secondary-dark text-white text-[10px] font-bold px-3 py-1 rounded-md transition-colors"
                                >
                                  Validieren
                                </button>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "settings" && (
              <div className="space-y-12">
                {/* 1. Visuelle Identität & SEO */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <ImageIcon className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Design & SEO</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <ImageInput 
                        label="Website-Logo (Header & Footer)" 
                        value={settings.logo} 
                        onChange={(val) => updateSettings({ logo: val })} 
                      />
                      <ImageInput 
                        label="Favicon (Browser-Tab Icon)" 
                        value={settings.favicon} 
                        onChange={(val) => updateSettings({ favicon: val })} 
                      />
                      <ImageInput 
                        label="Hintergrundbild (Hero Startseite)" 
                        value={settings.propertyImages.hero} 
                        onChange={(val) => updateSettings({ propertyImages: { ...settings.propertyImages, hero: val } })} 
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Meta-Beschreibung (SEO)</label>
                        <textarea
                          value={settings.metaDescription}
                          onChange={(e) => updateSettings({ metaDescription: e.target.value })}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-xs h-20 outline-none focus:border-primary transition-colors resize-none"
                          placeholder="Beschreibung für Google..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Keywords (SEO)</label>
                        <input
                          type="text"
                          value={settings.metaKeywords}
                          onChange={(e) => updateSettings({ metaKeywords: e.target.value })}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-xs outline-none focus:border-primary transition-colors"
                          placeholder="Lotto, Villa, gewinnen..."
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* 2. Configuration du Tirage */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Bell className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Configuration du Tirage</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Website-Name</label>
                      <input
                        type="text"
                        value={settings.siteName}
                        onChange={(e) => updateSettings({ siteName: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Verkaufsende</label>
                      <input
                        type="date"
                        value={settings.drawClosingDate}
                        onChange={(e) => updateSettings({ drawClosingDate: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ziehungsdatum</label>
                      <input
                        type="date"
                        value={settings.nextDrawDate}
                        onChange={(e) => updateSettings({ nextDrawDate: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Notar / Gerichtsvollzieher</label>
                      <input
                        type="text"
                        value={settings.bailiffName}
                        onChange={(e) => updateSettings({ bailiffName: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ziehungsbanner (Text-Alerte)</label>
                      <input
                        type="text"
                        value={settings.alertText}
                        onChange={(e) => updateSettings({ alertText: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                        placeholder="Z.B.: Nur noch 4 Tage bis zur Verlosung!"
                      />
                    </div>
                  </div>
                </section>

                {/* 3. Rechtliche Seiten */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Shield className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Rechtliche Texte (Markdown)</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Allgemeine Geschäftsbedingungen (AGB)</label>
                      <textarea
                        value={settings.termsOfService}
                        onChange={(e) => updateSettings({ termsOfService: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono h-48 outline-none focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Datenschutzerklärung</label>
                      <textarea
                        value={settings.privacyPolicy}
                        onChange={(e) => updateSettings({ privacyPolicy: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono h-48 outline-none focus:border-primary transition-colors"
                      />
                    </div>
                  </div>
                </section>

                {/* 4. Coordonnées & Entreprise */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <ExternalLink className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Kontaktdaten & Support</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Unternehmensname</label>
                      <input type="text" value={settings.companyName} onChange={(e) => updateSettings({ companyName: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Adresse</label>
                      <input type="text" value={settings.contactAddress} onChange={(e) => updateSettings({ contactAddress: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Telefon</label>
                      <input type="text" value={settings.contactPhone} onChange={(e) => updateSettings({ contactPhone: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email Support</label>
                      <input type="text" value={settings.supportEmail} onChange={(e) => updateSettings({ supportEmail: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none" />
                    </div>
                  </div>
                </section>

                {/* 5. Zahlungsanbieter */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <CreditCard className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Zahlungsanbieter & Gateway</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <label className="font-bold text-sm">Stripe (Cartes Bancaires)</label>
                        <input type="checkbox" checked={settings.paymentProviders.stripeEnabled} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, stripeEnabled: e.target.checked } })} />
                      </div>
                      <div className="flex flex-col gap-4">
                        <input type="text" value={settings.paymentProviders.stripePublicKey} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, stripePublicKey: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Public Key (pk_...)" />
                        <input type="password" value={settings.paymentProviders.stripeSecretKey} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, stripeSecretKey: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Secret Key (sk_...)" />
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <label className="font-bold text-sm">PayPal</label>
                        <input type="checkbox" checked={settings.paymentProviders.paypalEnabled} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, paypalEnabled: e.target.checked } })} />
                      </div>
                      <div className="flex flex-col gap-4">
                        <input type="text" value={settings.paymentProviders.paypalClientId} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, paypalClientId: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Client ID" />
                        <input type="password" value={settings.paymentProviders.paypalSecretKey} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, paypalSecretKey: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Secret Key" />
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <label className="font-bold text-sm">Überweisung</label>
                        <input type="checkbox" checked={settings.paymentProviders.bankTransferEnabled} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, bankTransferEnabled: e.target.checked } })} />
                      </div>
                      <div className="flex flex-col gap-4">
                        <input type="text" value={settings.paymentProviders.bankHolder} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, bankHolder: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Kontoinhaber" />
                        <input type="text" value={settings.paymentProviders.bankIban} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, bankIban: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="IBAN" />
                        <input type="text" value={settings.paymentProviders.bankBic} onChange={(e) => updateSettings({ paymentProviders: { ...settings.paymentProviders, bankBic: e.target.value } })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="BIC" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* 6. Live Chat */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <i className="ri-message-3-line text-primary text-xl"></i>
                    <h3 className="text-lg font-bold text-slate-800">Support Chat</h3>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-bold">Standardmäßig Chat aktivieren</label>
                      <input type="checkbox" checked={settings.chatEnabled} onChange={(e) => updateSettings({ chatEnabled: e.target.checked })} />
                    </div>
                    <div className="flex flex-col gap-4">
                      <input type="text" value={settings.chatBotName} onChange={(e) => updateSettings({ chatBotName: e.target.value })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Bot-Name" />
                      <input type="text" value={settings.chatResponseTime} onChange={(e) => updateSettings({ chatResponseTime: e.target.value })} className="bg-white border rounded-lg px-3 py-2 text-xs" placeholder="Reaktionszeit" />
                    </div>
                    <textarea value={settings.chatWelcomeMessage} onChange={(e) => updateSettings({ chatWelcomeMessage: e.target.value })} className="w-full bg-white border rounded-lg px-3 py-2 text-xs h-20" placeholder="Willkommensnachricht..." />
                    <div className="pt-4 border-t border-slate-200">
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Drittanbieter-Chat-Script (Crisp, Tidio...)</label>
                      <textarea value={settings.thirdPartyChatScript} onChange={(e) => updateSettings({ thirdPartyChatScript: e.target.value })} className="w-full bg-slate-900 text-secondary-light font-mono text-[10px] p-4 rounded-xl h-32" placeholder="<script>...</script>" />
                    </div>
                  </div>
                </section>

                {/* 7. Maintenance */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <LogOut className="text-red-500" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Wartung & Zugang</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-100">
                      <div>
                        <p className="text-sm font-bold text-red-800">Wartungsmodus</p>
                        <p className="text-[10px] text-red-600">Website für Kunden unzugänglich machen.</p>
                      </div>
                      <input type="checkbox" checked={settings.maintenanceMode} onChange={(e) => updateSettings({ maintenanceMode: e.target.checked })} className="secondary-light-red-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                      <div>
                        <p className="text-sm font-bold text-emerald-800">Anmeldungen offen</p>
                        <p className="text-[10px] text-secondary-dark">Erstellung von Konten erlauben.</p>
                      </div>
                      <input type="checkbox" checked={settings.registrationOpen} onChange={(e) => updateSettings({ registrationOpen: e.target.checked })} className="secondary-light-secondary-dark" />
                    </div>
                  </div>
                </section>

                {/* 8. Images Section Lifestyle */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <ImageIcon className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Lifestyle-Bilder (Galerie)</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {settings.lifestyleSlides?.map((slide, i) => (
                      <div key={i} className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                          <label className="text-xs font-bold uppercase tracking-wider">Image {i + 1}</label>
                          <button
                            onClick={() => {
                              const next = settings.lifestyleSlides.filter((_, idx) => idx !== i);
                              updateSettings({ lifestyleSlides: next });
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <ImageInput 
                          label={`Image`} 
                          value={slide.img} 
                          onChange={(val) => {
                            const next = [...settings.lifestyleSlides];
                            next[i].img = val;
                            updateSettings({ lifestyleSlides: next });
                          }} 
                        />
                        <input
                          type="text"
                          value={slide.label}
                          onChange={(e) => {
                            const next = [...settings.lifestyleSlides];
                            next[i].label = e.target.value;
                            updateSettings({ lifestyleSlides: next });
                          }}
                          placeholder="Label (z.B. Ziehungs-Haus)"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={slide.badgeTitle}
                            onChange={(e) => {
                              const next = [...settings.lifestyleSlides];
                              next[i].badgeTitle = e.target.value;
                              updateSettings({ lifestyleSlides: next });
                            }}
                            placeholder="Badge-Titel"
                            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          />
                          <input
                            type="text"
                            value={slide.badgeSub}
                            onChange={(e) => {
                              const next = [...settings.lifestyleSlides];
                              next[i].badgeSub = e.target.value;
                              updateSettings({ lifestyleSlides: next });
                            }}
                            placeholder="Badge-Untertitel"
                            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => updateSettings({ 
                      lifestyleSlides: [
                        ...(settings.lifestyleSlides || []), 
                        { img: "", label: "Neues Bild", badgeTitle: "Titel", badgeSub: "Beschreibung" }
                      ] 
                    })}
                    className="w-full border-2 border-dashed border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-primary font-bold py-3 rounded-xl transition-colors text-sm"
                  >
                    + Lifestyle-Bild hinzufügen
                  </button>
                </section>

                {/* 9. Accès Administrateur */}
                <section className="space-y-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Shield className="text-primary" size={20} />
                    <h3 className="text-lg font-bold text-slate-800">Admin-Zugang</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Login-E-Mail</label>
                      <input
                        type="email"
                        value={settings.adminEmail}
                        onChange={(e) => updateSettings({ adminEmail: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Passwort</label>
                      <input
                        type="text"
                        value={settings.adminPassword}
                        onChange={(e) => updateSettings({ adminPassword: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-primary transition-colors"
                        placeholder="Neues Passwort"
                      />
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === "chat" && (
              <div className="flex h-[600px] gap-6 -mt-4">
                {/* Conversations List */}
                <div className="w-80 border-r border-slate-100 pr-6 flex flex-col h-full overflow-hidden">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-4 px-2">Aktive Gespräche</p>
                  <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                    {conversations.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-10">Keine aktiven Gespräche</p>
                    ) : (
                      conversations.map(conv => (
                        <button
                          key={conv.session_id}
                          onClick={() => setActiveSession(conv.session_id)}
                          className={`w-full text-left p-3 rounded-xl transition-all cursor-pointer border ${activeSession === conv.session_id ? "bg-orange-50 border-orange-200" : "bg-white border-transparent hover:bg-slate-50"}`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-bold text-sm text-slate-900 truncate flex-1 leading-tight">
                              {conv.user_name || `Besucher ${conv.session_id.split('_')[1]}`}
                            </span>
                            {conv.unread_count > 0 && (
                              <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{conv.unread_count}</span>
                            )}
                          </div>
                          {conv.user_email && <p className="text-[10px] text-slate-400 mb-1">{conv.user_email}</p>}
                          <p className="text-xs text-slate-500 line-clamp-1">{conv.last_message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{new Date(conv.last_message_at).toLocaleTimeString()}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Chat Display */}
                <div className="flex-1 flex flex-col bg-slate-50/50 rounded-2xl border border-slate-100 overflow-hidden h-full">
                  {activeSession ? (
                    <>
                      <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {chatMessages.map(msg => (
                          <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${msg.sender === 'admin' ? 'bg-slate-900 text-white rounded-tr-sm' : (msg.sender === 'bot' ? 'bg-slate-100 text-slate-600 italic border border-slate-200' : 'bg-white text-slate-800 rounded-tl-sm border border-slate-100')}`}>
                               <p>{msg.text}</p>
                               <span className="text-[9px] opacity-60 mt-1 block h-3">{new Date(msg.created_at).toLocaleTimeString()} · {msg.sender}</span>
                            </div>
                          </div>
                        ))}
                        <div ref={chatBottomRef} />
                      </div>
                      <div className="p-4 bg-white border-t border-slate-100 flex gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendAdminReply()}
                          placeholder="Ihre Antwort hier..."
                          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors"
                        />
                        <button
                          onClick={sendAdminReply}
                          disabled={!chatInput.trim()}
                          className="bg-primary hover:bg-primary-dark disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer transition-colors"
                        >
                          Antworten
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10 text-center">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                        <MessageSquare size={32} />
                      </div>
                      <p className="font-bold text-slate-900 mb-1 font-playfair text-xl">Nachrichtenzentrum</p>
                      <p className="text-sm max-w-xs leading-relaxed">Wählen Sie eine Konversation aus der Liste links aus, um einen direkten Dialog mit einem Kunden zu beginnen.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <Footer />
    </main>
  );
}
